import React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { CardDetailFragment, ChangeCardholderPhoneDocument } from 'generated/resources/graphql'

import LabeledInput from 'modules/core/components/LabeledInput'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DialCodeSelector from 'modules/expenses/components/common/DialCodeSelector'

import { SidebarSection } from 'modules/banking/components/CardSidepanel/_Content/_SidebarSection'

type ChangePhoneFormValues = {
  id: string
  phoneCountryCode: string
  phoneNumber: string
}

const useChangePhoneNumber = (cardholder: CardDetailFragment['cardholder']) => {
  const [changeAlias, { loading: submitting }] = useMutation(ChangeCardholderPhoneDocument, {
    extractorFn: (data) => data.bankingMutations.changePhoneBankingCardholder
  })

  const form = useForm<ChangePhoneFormValues>({
    defaultValues: {
      id: cardholder.id + '',
      phoneCountryCode: cardholder.phoneCountryCode ?? '',
      phoneNumber: cardholder.phoneNumber ?? ''
    },
    validate: (data) => {
      const errors: FormErrors<ChangePhoneFormValues> = {}
      if (!data.phoneNumber) {
        errors.phoneNumber = i18n.t('common.validations.empty')
      }
      if (!data.phoneCountryCode) {
        errors.phoneCountryCode = i18n.t('common.validations.empty')
      }
      return errors
    },
    onSubmit: async (values) => {
      const result = await changeAlias({ variables: values })
      const handled = handleResult(result, (errors) => {
        form.setErrors({
          phoneNumber: errors['phoneNumber']
        })
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully')
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving')
        })
      }
      return handled
    }
  })

  const readonly = !cardholder._policies?.changePhone?.can
  const submitDisabled = readonly || !form.dirty || submitting || form.isSubmitting || !form.isValid
  const onSubmit = () => form.handleSubmit()
  const onPhoneNumberChange = (value: string) => form.setFieldValue('phoneNumber', value)
  const onPhoneCountryCodeChange = (value: string) => form.setFieldValue('phoneCountryCode', value)

  return { form, submitDisabled, onSubmit, onPhoneCountryCodeChange, onPhoneNumberChange, readonly }
}

export const Cardholder: React.FC<{ cardholder: CardDetailFragment['cardholder'] }> = ({
  cardholder
}) => {
  const {
    form,
    submitDisabled,
    onSubmit,
    onPhoneCountryCodeChange,
    onPhoneNumberChange,
    readonly
  } = useChangePhoneNumber(cardholder)

  return (
    <SidebarSection>
      <Box width="full" paddingBottom="s24">
        <Text.Normal weight="semibold" size="300" color="textPrimary">
          {i18n.t('banking.cards.cardholder_information')}
        </Text.Normal>
      </Box>
      <Box flexDirection="row" gap="s24" width="full">
        <Box gap="s6">
          <DialCodeSelector
            readOnly={readonly}
            label={i18n.t('expenses_v2.forms.fields.country_code.label')}
            locale={cardholder.employee?.access?.locale ?? 'en'}
            value={form.values.phoneCountryCode}
            onChange={(value) => onPhoneCountryCodeChange(value)}
            mandatory
          />
          <Text color="grey700" size="200">
            {i18n.t('expenses_v2.forms.fields.sending_options.country_phone_number_example')}
          </Text>
        </Box>
        <Box gap="s6" flexGrow>
          <LabeledInput
            isLegend
            label={i18n.t('expenses_v2.forms.fields.mobile_phone_number.label')}
          >
            <TextField
              value={form.values.phoneNumber}
              error={form.touched.phoneNumber ? form.errors.phoneNumber : undefined}
              onChange={onPhoneNumberChange}
              mandatory
              readonly={readonly}
            />
          </LabeledInput>
          <Text color="grey700" size="200">
            {i18n.t('expenses_v2.forms.fields.sending_options.phone_number_example')}
          </Text>
        </Box>
      </Box>

      {!readonly && (
        <Box width="full" flexDirection="rowReverse">
          <Button
            type="secondary"
            onClick={onSubmit}
            disabled={submitDisabled}
            aria-label={i18n.t('common.update')}
          >
            {i18n.t('common.update')}
          </Button>
        </Box>
      )}
    </SidebarSection>
  )
}
